import { InvoicesState } from './types';
import { ActionTree } from 'vuex';
import { RootState } from '../../types';
import { Logger } from 'fsts';
import { DefaultBackendInvoice } from '@/shared/backend/invoice';
import {
  Invoice,
  Value,
  InvoicePaymentStatus,
  InvoicePeriodType,
} from '@/shared/model/invoice';
import { AxiosResponse } from 'axios';
import moduleUtils from '../moduleUtils';
import { i18nGlobal } from '@/i18n';
import DownloadUtils from '@/shared/utils/downloadUtils';
import { createSearchParams } from '@/shared/model/searchParams';
import DateUtils from '@/shared/utils/dateUtils';

const logger = new Logger('actions.invoices');
export const actions: ActionTree<InvoicesState, RootState> = {
  getInvoices(
    { commit, getters },
    payload?: {
      searchParams?: any;
      attendeeAppUserId?: string;
      filterByStatuses?: InvoicePaymentStatus[];
      dateFrom?: string;
      dateTill?: string;
      locationId?: string;
      departmentId?: string;
      dueDateBefore?: string;
      invoiceRunId?: string;
    }
  ) {
    commit('setInvoicesIsLoading', true);
    let searchParams = payload?.searchParams ?? getters.getInvoicesSearchParams;

    return DefaultBackendInvoice.getInvoices(
      searchParams,
      payload?.attendeeAppUserId,
      payload?.filterByStatuses,
      payload?.dateFrom,
      payload?.dateTill,
      payload?.locationId,
      payload?.departmentId,
      payload?.dueDateBefore,
      payload?.invoiceRunId
    )
      .then((response: AxiosResponse<Value>) => {
        commit('setInvoices', response.data);
        return response.data;
      })
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      })
      .finally(() => {
        commit('setInvoicesIsLoading', false);
      });
  },
  updateInvoice({ commit }, invoice: Invoice) {
    return DefaultBackendInvoice.updateInvoice(invoice)
      .then(
        (
          response: AxiosResponse<{
            errors: [];
            id: string;
            isValid: boolean;
          }>
        ) => {
          moduleUtils.ok(i18nGlobal.t(`success.invoice_update`), commit);
          return response.data;
        }
      )
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  async exportCompleteInvoices(
    { commit },
    payload: {
      markExportedInvoicesPaid: boolean;
      newDueDateOnExportedInvoices: undefined | string;
      exportInSingleFile: boolean;
      exportPreviouslyExportedInvoices: boolean;
      exportIncompleteInvoices: boolean;
      exportForMonth: number | undefined;
      exportForYear: number | undefined;
      exportForAttendeeIds: string[] | undefined;
      exportWithPaymentStatuses: InvoicePaymentStatus[] | undefined;
      exportWithPeriodTypes: InvoicePeriodType[] | undefined;
      invoiceRunId: string | undefined;
    }
  ) {
    commit('setInvoicesIsLoading', true);
    let culture = i18nGlobal.locale.value == 'de' ? 'de-DE' : 'en-US';
    try {
      await DefaultBackendInvoice.exportCompleteInvoices(
        payload.markExportedInvoicesPaid,
        payload.newDueDateOnExportedInvoices,
        payload.exportInSingleFile,
        payload.exportPreviouslyExportedInvoices,
        payload.exportIncompleteInvoices,
        payload.exportForMonth,
        payload.exportForYear,
        payload.exportForAttendeeIds,
        payload.exportWithPaymentStatuses,
        payload.exportWithPeriodTypes,
        payload.invoiceRunId,
        culture
      ).then((response) => {
        let fileName = DownloadUtils.getFileName(
          response.headers['content-disposition']
        );
        const type = response.headers['content-type'];
        if (DownloadUtils.isIeOrEdge(window)) {
          const file = new File([response.data], fileName, {
            type: type,
          });
          (window.navigator as any).msSaveOrOpenBlob(file, fileName);
        } else {
          const blob = new Blob([response.data], { type: type });
          const url = DownloadUtils.createDownloadLink(blob, fileName);
          window.URL.revokeObjectURL(url);
        }
        moduleUtils.ok(i18nGlobal.t(`success.invoice_export`), commit);
      });
    } catch (e) {
      logger.error(e);
      moduleUtils.error(i18nGlobal.t(`error.download_file`), commit, e, logger);
      throw e;
    } finally {
      commit('setInvoicesIsLoading', false);
    }
  },
  async exportSingleInvoice({ commit }, invoiceId: string) {
    const culture = i18nGlobal.locale.value == 'de' ? 'de-DE' : 'en-US';
    try {
      await DefaultBackendInvoice.exportSingleInvoice(invoiceId, culture).then(
        (response) => {
          const fileName = DownloadUtils.getFileName(
            response.headers['content-disposition']
          );
          const type = response.headers['content-type'];
          if (DownloadUtils.isIeOrEdge(window)) {
            const file = new File([response.data], fileName, {
              type: type,
            });
            (window.navigator as any).msSaveOrOpenBlob(file, fileName);
          } else {
            const blob = new Blob([response.data], { type: type });
            const url = DownloadUtils.createDownloadLink(blob, fileName);
            window.URL.revokeObjectURL(url);
          }
        }
      );
    } catch (e) {
      logger.error(e);
      moduleUtils.error(i18nGlobal.t(`error.download_file`), commit, e, logger);
      throw e;
    } finally {
      commit('setInvoicesIsLoading', false);
    }
  },
  countCompleteNonExportedInvoices(
    { commit },
    params: {
      exportPreviouslyExportedInvoices: boolean;
      exportIncompleteInvoices: boolean;
      exportForMonth: number | undefined;
      exportForYear: number | undefined;
      exportForAttendeeIds: string[] | undefined;
      exportWithPaymentStatuses: InvoicePaymentStatus[] | undefined;
      exportWithPeriodTypes: InvoicePeriodType[] | undefined;
      invoiceRunId: string | undefined;
    }
  ) {
    return DefaultBackendInvoice.countCompleteNonExportedInvoices(
      params.exportPreviouslyExportedInvoices,
      params.exportIncompleteInvoices,
      params.exportForMonth,
      params.exportForYear,
      params.exportForAttendeeIds,
      params.exportWithPaymentStatuses,
      params.exportWithPeriodTypes,
      params.invoiceRunId
    )
      .then((resp) => resp.data)
      .catch((e) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  getUnpaidInvoicesTotalOpenCount(
    { dispatch, commit },
    payload: { locationId?: string; departmentId?: string }
  ) {
    dispatch('getInvoices', {
      locationId: payload?.locationId,
      departmentId: payload?.departmentId,
      filterByStatuses: [
        InvoicePaymentStatus.Open,
        InvoicePaymentStatus.PartiallyPaid,
      ],
      dueDateBefore: DateUtils.getTodayDateAsIsoString(),
      searchParams: createSearchParams(0),
      noStore: true,
    }).then((resp: Value) => {
      commit('setUnpaidInvoicesTotalOpenCount', resp);
      return resp['@odata.count'];
    });
  },
  getStartDateOfFollowUpInvoice(
    { commit },
    payload: {
      periodType: InvoicePeriodType;
      inputDate: string;
      attendeeAppUserId: string;
    }
  ) {
    return DefaultBackendInvoice.getStartDateOfFollowUpInvoice(
      payload.periodType,
      payload.inputDate,
      payload.attendeeAppUserId
    )
      .then((resp) => resp.data)
      .catch((e) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
};
